// src/components/GroupChatModal.tsx
import { logger } from '../utils/logger';
// ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ ENHANCED: Added report functionality for groups
// ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂºÃ‚Â¡ÃƒÂ¯Ã‚Â¸Ã‚Â PROTECTION: Auto-leave group after reporting
// ÃƒÂ°Ã…Â¸Ã¢â‚¬ËœÃ‚Â» ANONYMIZATION: Handles deleted users (messages with sender_id = NULL)

import React, { useState, useEffect, useRef } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonFooter,
  IonTextarea,
  IonSpinner,
  IonText,
  IonAvatar,
  IonActionSheet,
  IonAlert,
  IonToast,
} from '@ionic/react';
import {
  close,
  send,
  ellipsisVertical,
  peopleOutline,
  exitOutline,
  volumeMute,
  notifications,
  flagOutline,
  imageOutline,
  settingsOutline,
  addCircleOutline,
} from 'ionicons/icons';
import { ImpactStyle } from '@capacitor/haptics';
import { Keyboard } from '@capacitor/keyboard';
import {
  sendGroupMessage,
  getGroupMessages,
  getGroupDetails,
  subscribeToGroupMessages,
  unsubscribeFromGroupMessages,
  leaveGroup,
  GroupMessage,
  GroupWithDetails,
} from '../services/groupService';
import { supabase } from '../services/supabase';
import { muteChat, unmuteChat, isMuted, MuteDuration } from '../services/muteService';
import { markGroupMessagesAsRead } from '../services/unreadService';
import { AvatarPreview } from './avatar/AvatarPreview';
import { UserProfileModal } from './UserProfileModal';
import { ReportModal } from './ReportModal';
import { moderateChatMessage } from '../services/contentModeration';
import { getGroupImageConsent, toggleGroupImageSharing } from '../services/imageConsentService';
import { MessageReactions } from './MessageReactions';
import {
  getGroupMessageReactions,
  addGroupMessageReaction,
  removeGroupMessageReaction,
  ReactionSummary
} from '../services/reactionsService';
import { uploadFileWithRetry, sendMessageWithRetry } from '../services/retryService';
import { handleError, ErrorMessages } from '../services/errorHandler';
import { themeService } from '../services/themeService';
import './GroupChatModal.css';

// Rainbow color palette for pride theme - cycles through all 6 colors sequentially
const RAINBOW_COLORS = [
  { bg: '#ef4444', text: '#ffffff' }, // red
  { bg: '#f97316', text: '#ffffff' }, // orange
  { bg: '#eab308', text: '#000000' }, // yellow
  { bg: '#22c55e', text: '#000000' }, // green
  { bg: '#3b82f6', text: '#ffffff' }, // blue
  { bg: '#a855f7', text: '#ffffff' }, // purple
];

// Get color based on message index (only for pride theme)
const getMessageColor = (index: number) => {
  return RAINBOW_COLORS[index % RAINBOW_COLORS.length];
};

interface GroupChatModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  groupId: string;
  onMembersClick?: () => void;
  onGroupLeft?: () => void;
}

export const GroupChatModal: React.FC<GroupChatModalProps> = ({
  isOpen,
  onDismiss,
  groupId,
  onMembersClick,
  onGroupLeft,
}) => {
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [group, setGroup] = useState<GroupWithDetails | null>(null);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showMuteAlert, setShowMuteAlert] = useState(false);
  const [isChatMuted, setIsChatMuted] = useState(false);
  const [mutedUntil, setMutedUntil] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [showReportModal, setShowReportModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedProfileUserId, setSelectedProfileUserId] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showSettingsAlert, setShowSettingsAlert] = useState(false);
  const [imagesEnabled, setImagesEnabled] = useState(false);
  const [isGroupLeader, setIsGroupLeader] = useState(false);
  const [messageReactions, setMessageReactions] = useState<{ [messageId: string]: ReactionSummary[] }>({});
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [isPrideTheme, setIsPrideTheme] = useState(false);
  const [screenReaderAnnouncement, setScreenReaderAnnouncement] = useState('');
  const contentRef = useRef<HTMLIonContentElement>(null);
  const textareaRef = useRef<HTMLIonTextareaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subscriptionRef = useRef<any>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const typingChannelRef = useRef<any>(null);
  const markAsReadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let mounted = true;

    if (isOpen) {
      const init = async () => {
        await getCurrentUser();
        await loadGroupDetails();
        await loadImageConsentStatus();
        await checkMuteStatus();
        await loadMessages();
        await markAsRead();
        if (mounted) {
          setupSubscription();
          setupTypingChannel();
        }
      };
      init();
    } else {
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
        markAsReadTimeoutRef.current = null;
      }
    }

    return () => {
      mounted = false;
      if (subscriptionRef.current) {
        logger.log('Unsubscribing from group messages');
        unsubscribeFromGroupMessages(subscriptionRef.current);
        subscriptionRef.current = null;
      }
      if (typingChannelRef.current) {
        supabase.removeChannel(typingChannelRef.current);
        typingChannelRef.current = null;
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
      }
    };
  }, [isOpen, groupId]);

  // Check if pride theme is active for rainbow cycling
  useEffect(() => {
    const checkTheme = () => {
      const currentPreset = themeService.getThemePreset();
      setIsPrideTheme(currentPreset === 'pride');
    };

    checkTheme();
    // Check theme when modal opens
    if (isOpen) {
      checkTheme();
    }
  }, [isOpen]);

  // Focus management: Focus textarea when modal opens and loading completes
  useEffect(() => {
    if (isOpen && !loading) {
      setTimeout(() => {
        textareaRef.current?.setFocus();
      }, 300); // Delay to allow modal animation to complete
    }
  }, [isOpen, loading]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUserId(user.id);
    }
  };

  const loadGroupDetails = async () => {
    const result = await getGroupDetails(groupId);
    if (result.success && result.group) {
      setGroup(result.group);

      // Check if current user is group leader (creator or admin)
      const member = result.group.members?.find(m => m.user_id === currentUserId);
      if (member && (member.role === 'creator' || member.role === 'admin')) {
        setIsGroupLeader(true);
      }
    }
  };

  const loadImageConsentStatus = async () => {
    const result = await getGroupImageConsent(groupId);
    if (result.success && result.enabled !== undefined) {
      setImagesEnabled(result.enabled);
    }
  };

  const checkMuteStatus = async () => {
    const muteStatus = await isMuted(groupId, 'group');
    if (muteStatus.success) {
      setIsChatMuted(muteStatus.muted);
      setMutedUntil(muteStatus.mutedUntil || null);
    }
  };

  const loadMessages = async () => {
    setLoading(true);
    const result = await getGroupMessages(groupId);
    if (result.success && result.messages) {
      setMessages(result.messages);
      setHasMoreMessages(result.hasMore ?? false);
      setTimeout(() => scrollToBottom(), 100);
      loadReactionsForMessages(result.messages);
    }
    setLoading(false);
  };

  const loadOlderMessages = async () => {
    if (loadingMore || !hasMoreMessages || messages.length === 0) return;
    setLoadingMore(true);
    const oldestTimestamp = messages[0].created_at;
    const result = await getGroupMessages(groupId, oldestTimestamp);
    if (result.success && result.messages && result.messages.length > 0) {
      setMessages(prev => [...result.messages!, ...prev]);
      setHasMoreMessages(result.hasMore ?? false);
      loadReactionsForMessages(result.messages);
    }
    setLoadingMore(false);
  };

  const loadReactionsForMessages = async (msgs: GroupMessage[]) => {
    // Load all reactions in parallel for better performance
    const results = await Promise.all(
      msgs.map(msg => getGroupMessageReactions(msg.id))
    );

    const reactionsMap: { [messageId: string]: ReactionSummary[] } = {};
    msgs.forEach((msg, index) => {
      const result = results[index];
      if (result.success && result.data) {
        reactionsMap[msg.id] = result.data;
      }
    });

    setMessageReactions(reactionsMap);
  };

  const handleAddReaction = async (messageId: string, emoji: string) => {
    const result = await addGroupMessageReaction(messageId, emoji);
    if (result.success) {
      // Reload reactions for this message
      const reactionsResult = await getGroupMessageReactions(messageId);
      if (reactionsResult.success && reactionsResult.data) {
        setMessageReactions(prev => ({
          ...prev,
          [messageId]: reactionsResult.data!
        }));
      }
    }
  };

  const handleRemoveReaction = async (messageId: string, emoji: string) => {
    const result = await removeGroupMessageReaction(messageId, emoji);
    if (result.success) {
      // Reload reactions for this message
      const reactionsResult = await getGroupMessageReactions(messageId);
      if (reactionsResult.success && reactionsResult.data) {
        setMessageReactions(prev => ({
          ...prev,
          [messageId]: reactionsResult.data!
        }));
      }
    }
  };

  const markAsRead = async () => {
    logger.log('Marking group messages as read for:', groupId);
    const result = await markGroupMessagesAsRead(groupId);
    if (!result.success) {
      logger.error('Failed to mark group messages as read:', result.error);
    }
  };

  const setupSubscription = () => {
    logger.log('Setting up group subscription for:', groupId);

    subscriptionRef.current = subscribeToGroupMessages(groupId, async (message) => {
      logger.log('Received group message:', message.id);

      setMessages(prev => {
        const exists = prev.find(m => m.id === message.id);
        if (exists) {
          logger.log('Message already exists, skipping:', message.id);
          return prev;
        }
        logger.log('Adding new group message:', message.id);
        return [...prev, message];
      });

      setTimeout(() => scrollToBottom(), 100);

      if (isOpen && message.sender_id !== currentUserId) {
        await markAsRead();

        // Announce new message to screen readers
        const senderName = message.sender?.display_name || message.sender?.username || 'Someone';
        setScreenReaderAnnouncement(`New message from ${senderName}: ${message.content}`);
      }
    });
  };

  const setupTypingChannel = () => {
    const channel = supabase.channel(`typing:group:${groupId}`, {
      config: {
        broadcast: { self: false },
      },
    });

    channel
      .on('broadcast', { event: 'typing' }, ({ payload }) => {
        if (payload.userId !== currentUserId) {
          setTypingUsers(prev => {
            const next = new Set(prev);
            next.add(payload.userId);
            return next;
          });

          setTimeout(() => {
            setTypingUsers(prev => {
              const next = new Set(prev);
              next.delete(payload.userId);
              return next;
            });
          }, 3000);
        }
      })
      .subscribe();

    typingChannelRef.current = channel;
  };

  const handleTyping = () => {
    if (!isTyping) {
      setIsTyping(true);
      typingChannelRef.current?.send({
        type: 'broadcast',
        event: 'typing',
        payload: { userId: currentUserId },
      });
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 3000);
  };

  const scrollToBottom = () => {
    contentRef.current?.scrollToBottom(300);
  };

  const handleTextareaFocus = () => {
    setTimeout(() => scrollToBottom(), 300);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    // ðŸ›¡ï¸ Content moderation check
    const contentCheck = moderateChatMessage(newMessage.trim());
    if (!contentCheck.isClean) {
      setToastMessage('âš ï¸ Stop that! Message contains inappropriate language.');
      setShowToast(true);
      return;
    }

    setSending(true);

    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      // Send message with retry logic
      const result = await sendMessageWithRetry(
        () => sendGroupMessage(groupId, messageText)
      );

      if (result.success && result.data?.message) {
        logger.log('Group message sent:', result.data.message.id);
        setMessages(prev => {
          if (prev.find(m => m.id === result.data!.message!.id)) {
            return prev;
          }
          return [...prev, result.data!.message!];
        });
        setTimeout(() => scrollToBottom(), 100);

        setTimeout(() => {
          textareaRef.current?.setFocus();
        }, 100);
      } else {
        logger.error('Failed to send group message:', result.error);
        const errorMsg = result.error?.userMessage || ErrorMessages.MESSAGE_SEND_FAILED;
        setToastMessage(errorMsg);
        setShowToast(true);
        setNewMessage(messageText);
      }
    } catch (error) {
      logger.error('Error sending group message:', error);
      const appError = handleError(error, 'send message');
      setToastMessage(appError.userMessage);
      setShowToast(true);
      setNewMessage(messageText);
    }

    setSending(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleContentClick = async () => {
    try {
      await Keyboard.hide();
    } catch (error) {
      // Keyboard might not be available on web
    }
  };

  const handleMute = async (duration: MuteDuration) => {
    const result = await muteChat(groupId, 'group', duration);
    if (result.success) {
      setIsChatMuted(true);
      await checkMuteStatus();
    } else {
      alert('Failed to mute notifications. Please try again.');
    }
  };

  const handleUnmute = async () => {
    const result = await unmuteChat(groupId, 'group');
    if (result.success) {
      setIsChatMuted(false);
      setMutedUntil(null);
    } else {
      alert('Failed to unmute notifications. Please try again.');
    }
  };

  const handleLeaveGroup = async () => {
    setShowActionSheet(false);

    const confirmed = window.confirm('Are you sure you want to leave this group?');
    if (!confirmed) return;

    const result = await leaveGroup(groupId);
    if (result.success) {
      if (onGroupLeft) onGroupLeft();
      onDismiss();
    } else {
      alert('Failed to leave group. Please try again.');
    }
  };

  const handleReportComplete = () => {
    setShowReportModal(false);
    setToastMessage('Report submitted successfully');
    setShowToast(true);
    setTimeout(() => {
      handleLeaveGroup();
    }, 1500);
  };

  const handleToggleImageSharing = async () => {
    setShowSettingsAlert(false);
    const newState = !imagesEnabled;
    const result = await toggleGroupImageSharing(groupId, newState);

    if (result.success) {
      setImagesEnabled(newState);
      setToastMessage(newState ? 'Image sharing enabled for this group' : 'Image sharing disabled for this group');
      setShowToast(true);
    } else {
      setToastMessage(result.error || 'Failed to update image sharing setting');
      setShowToast(true);
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setToastMessage('Please select an image file');
      setShowToast(true);
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setToastMessage('Image must be smaller than 5MB');
      setShowToast(true);
      return;
    }

    // TODO: Implement image upload to storage and send as message
    // For now, just show a placeholder message
    setToastMessage('Image upload feature coming soon!');
    setShowToast(true);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const getTypingText = () => {
    const count = typingUsers.size;
    if (count === 0) return null;
    if (count === 1) return 'Someone is typing...';
    if (count === 2) return '2 people are typing...';
    return 'Several people are typing...';
  };

  return (
    <>
      {/* Screen reader announcement region */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {screenReaderAnnouncement}
      </div>

      <IonModal isOpen={isOpen} onDidDismiss={onDismiss} keyboardClose={false}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={onDismiss} aria-label="Close group chat">
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
            <IonTitle style={{ paddingLeft: '60px' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>{group?.name || 'Group Chat'}</span>
                  <IonText color="medium" style={{ fontSize: '12px' }}>
                    ({group?.member_count || 0})
                  </IonText>
                  {isChatMuted && (
                    <IonIcon icon={volumeMute} style={{ fontSize: '16px', color: 'var(--ion-color-medium)' }} aria-label="Group is muted" />
                  )}
                </div>
                {typingUsers.size > 0 && (
                  <span style={{ fontSize: '12px', color: 'var(--ion-color-medium)' }}>
                    {getTypingText()}
                  </span>
                )}
              </div>
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowActionSheet(true)} aria-label="Open group chat options">
                <IonIcon icon={ellipsisVertical} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent
          ref={contentRef}
          className="group-chat-content"
          scrollEvents={true}
          onClick={handleContentClick}
        >
          {/* Image Sharing Status Banner */}
          {imagesEnabled && (
            <div style={{
              padding: '8px 16px',
              backgroundColor: 'var(--ion-color-success)',
              borderBottom: '1px solid var(--ion-color-success-shade)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <IonIcon icon={imageOutline} style={{ color: 'var(--ion-color-success-contrast)' }} />
              <IonText style={{ fontSize: '13px', color: 'var(--ion-color-success-contrast)' }}>
                Image sharing enabled for this group
              </IonText>
            </div>
          )}

          {loading ? (
            <div className="group-chat-loading">
              <IonSpinner name="crescent" />
            </div>
          ) : messages.length === 0 ? (
            <div className="group-chat-empty">
              <IonIcon
                icon={send}
                style={{ fontSize: '60px', marginBottom: '15px', opacity: 0.5, color: 'var(--ion-color-medium)' }}
              />
              <h3 style={{ fontSize: '18px', marginBottom: '8px' }}>No messages yet</h3>
              <p style={{ fontSize: '14px', color: 'var(--ion-color-medium)' }}>Start the conversation!</p>
            </div>
          ) : (
            <div className="group-messages-container">
              {hasMoreMessages && (
                <div style={{ textAlign: 'center', padding: '8px' }}>
                  <IonButton
                    fill="clear"
                    size="small"
                    disabled={loadingMore}
                    onClick={loadOlderMessages}
                  >
                    {loadingMore ? <IonSpinner name="crescent" /> : 'Load older messages'}
                  </IonButton>
                </div>
              )}
              {messages.map((message, index) => {
                  // ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ Handle deleted users: if sender_id is NULL, it's a deleted user
                  const isDeletedUser = message.sender_id === null;
                const isMe = !isDeletedUser && message.sender_id === currentUserId;
                const showAvatar =
                  index === 0 || messages[index - 1].sender_id !== message.sender_id;
                const showName =
                  !isMe &&
                  (index === 0 || messages[index - 1].sender_id !== message.sender_id);

                // For deleted users, show in center with special styling
                if (isDeletedUser) {
                  return (
                    <div
                      key={message.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: '12px',
                        padding: '0 16px'
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '80%',
                          padding: '10px 14px',
                          borderRadius: '18px',
                          backgroundColor: 'var(--ion-color-light-shade)',
                          color: 'var(--ion-color-medium)',
                          opacity: 0.7,
                        }}
                      >
                        <div style={{
                          fontSize: '13px',
                          marginBottom: '4px',
                          fontStyle: 'italic',
                          opacity: 0.8
                        }}>
                          [Deleted User]
                        </div>
                        <p style={{ margin: 0, marginBottom: '4px', lineHeight: '1.4', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {message.content}
                        </p>
                        <span style={{ fontSize: '13px', opacity: 0.7, display: 'block', textAlign: 'right' }}>
                          {formatTime(message.created_at)}
                        </span>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={message.id}
                    className={`group-message ${isMe ? 'group-message-sent' : 'group-message-received'}`}
                  >
                    {!isMe && showAvatar && (
                      <IonAvatar
                        className="group-message-avatar"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (message.sender_id && message.sender_id !== currentUserId) {
                            setSelectedProfileUserId(message.sender_id);
                            setShowProfileModal(true);
                          }
                        }}
                      >
                        {message.sender?.avatar_url ? (
                          <img
                            src={message.sender.avatar_url}
                            alt={message.sender.display_name || message.sender.username}
                          />
                        ) : message.sender?.avatar ? (
                            <AvatarPreview
                              avatarData={message.sender.avatar}
                              size={32}
                              userId={message.sender_id}
                              isAdmin={message.sender.is_admin || false}
                            />
                        ) : (
                          <AvatarPreview size={32} userId={message.sender_id} />
                        )}
                      </IonAvatar>
                    )}
                    <div className="group-message-content">
                      {showName && message.sender && (
                        <div className="group-message-sender">
                          {message.sender.display_name || message.sender.username}
                        </div>
                      )}
                      {showName && !message.sender && (
                        <div className="group-message-sender" style={{ fontStyle: 'italic', color: 'var(--ion-color-medium)' }}>
                          [Deleted User]
                        </div>
                      )}
                      <div
                        className="group-message-bubble"
                        onClick={() => setActiveMessageId(message.id === activeMessageId ? null : message.id)}
                        style={{
                          cursor: 'pointer',
                          // Only use rainbow colors if pride theme is active
                          ...(isPrideTheme ? {
                            backgroundColor: getMessageColor(index).bg,
                            color: getMessageColor(index).text,
                          } : {})
                        }}
                      >
                        <p>{message.content}</p>
                        <span className="group-message-time">
                          {formatTime(message.created_at)}
                        </span>
                        {/* Emoji Reactions */}
                        <MessageReactions
                          reactions={messageReactions[message.id] || []}
                          currentUserId={currentUserId}
                          onAddReaction={(emoji) => handleAddReaction(message.id, emoji)}
                          onRemoveReaction={(emoji) => handleRemoveReaction(message.id, emoji)}
                          isActive={activeMessageId === message.id}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </IonContent>

        <IonFooter className="group-chat-footer">
          <div className="group-chat-input-container">
            {/* Hidden file input for image selection */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageSelect}
            />

            {/* Image upload button - only shown when images are enabled */}
            {imagesEnabled && (
              <IonButton
                fill="clear"
                onClick={handleImageButtonClick}
                disabled={sending}
                aria-label="Add image"
                style={{ margin: '0 4px 0 0' }}
              >
                <IonIcon icon={addCircleOutline} slot="icon-only" />
              </IonButton>
            )}

            <IonTextarea
              ref={textareaRef}
              value={newMessage}
              onIonInput={(e) => {
                setNewMessage(e.detail.value || '');
                handleTyping();
              }}
              onIonFocus={handleTextareaFocus}
              onKeyDown={handleKeyPress}
              placeholder="Type a message..."
              autoGrow
              rows={1}
              maxlength={500}
              autocapitalize="sentences"
              disabled={sending}
            />
            <IonButton
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              aria-label="Send message"
            >
              {sending ? <IonSpinner name="crescent" /> : <IonIcon icon={send} />}
            </IonButton>
          </div>
        </IonFooter>
      </IonModal>

      <IonActionSheet
        isOpen={showActionSheet}
        onDidDismiss={() => setShowActionSheet(false)}
        header="Group Options"
        buttons={[
          {
            text: 'View Members',
            icon: peopleOutline,
            handler: () => {
              if (onMembersClick) onMembersClick();
            },
          },
          ...(isGroupLeader ? [{
            text: 'Group Settings',
            icon: settingsOutline,
            handler: () => {
              setShowSettingsAlert(true);
            },
          }] : []),
          {
            text: isChatMuted ? 'Unmute' : 'Mute',
            icon: isChatMuted ? notifications : volumeMute,
            handler: () => {
              if (isChatMuted) {
                handleUnmute();
              } else {
                setShowMuteAlert(true);
              }
            },
          },
          {
            text: 'Report Group',
            icon: flagOutline,
            role: 'destructive',
            handler: () => {
              setShowReportModal(true);
            },
          },
          {
            text: 'Leave Group',
            icon: exitOutline,
            role: 'destructive',
            handler: handleLeaveGroup,
          },
          {
            text: 'Cancel',
            role: 'cancel',
          },
        ]}
      />

      <IonAlert
        isOpen={showMuteAlert}
        onDidDismiss={() => setShowMuteAlert(false)}
        header="Mute Notifications"
        message="How long would you like to mute notifications from this group?"
        buttons={[
          {
            text: '1 hour',
            handler: () => handleMute('1hour'),
          },
          {
            text: '8 hours',
            handler: () => handleMute('8hours'),
          },
          {
            text: '24 hours',
            handler: () => handleMute('24hours'),
          },
          {
            text: '1 week',
            handler: () => handleMute('1week'),
          },
          {
            text: 'Until I unmute',
            handler: () => handleMute('indefinite'),
          },
          {
            text: 'Cancel',
            role: 'cancel',
          },
        ]}
      />

      <IonAlert
        isOpen={showSettingsAlert}
        onDidDismiss={() => setShowSettingsAlert(false)}
        header="Group Settings"
        message="Configure group settings"
        buttons={[
          {
            text: imagesEnabled ? 'Disable Image Sharing' : 'Enable Image Sharing',
            handler: handleToggleImageSharing,
          },
          {
            text: 'Cancel',
            role: 'cancel',
          },
        ]}
      />

      <ReportModal
        isOpen={showReportModal}
        onDismiss={() => setShowReportModal(false)}
        reportType="group"
        reportedId={groupId}
        reportedName={group?.name || 'Group'}
        onReportComplete={handleReportComplete}
      />

      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={2000}
        color="success"
        aria-live="polite"
      />

      {selectedProfileUserId && (
        <UserProfileModal
          isOpen={showProfileModal}
          onDismiss={() => {
            setShowProfileModal(false);
            setSelectedProfileUserId(null);
          }}
          userId={selectedProfileUserId}
          currentUserId={currentUserId}
          showChatButton={false}
        />
      )}
    </>
  );
};